import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Star, MessageSquare, Loader2, ImagePlus, X } from "lucide-react";
import { invalidateReviewSummary } from "@/hooks/useReviewSummary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_PHOTOS = 4;

function Stars({ value, size = "w-4 h-4", interactive = false, onChange, dataTestid }) {
  const [hover, setHover] = useState(0);
  const active = hover || value;
  return (
    <div className="inline-flex items-center gap-0.5" data-testid={dataTestid}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= active;
        const half = !filled && i - 0.5 <= active;
        return (
          <button
            key={i}
            type="button"
            disabled={!interactive}
            onMouseEnter={() => interactive && setHover(i)}
            onMouseLeave={() => interactive && setHover(0)}
            onClick={() => interactive && onChange?.(i)}
            aria-label={`${i} star${i > 1 ? "s" : ""}`}
            className={`${interactive ? "cursor-pointer transition-transform hover:scale-110" : "cursor-default"} bg-transparent border-0 p-0`}
            data-testid={interactive ? `star-${i}` : undefined}
          >
            <Star
              className={`${size} ${
                filled || half ? "text-[#39FF14] fill-[#39FF14]" : "text-zinc-700"
              }`}
              style={filled ? { filter: "drop-shadow(0 0 4px rgba(57,255,20,0.6))" } : undefined}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function ReviewsSection({ productId }) {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(0);
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [photos, setPhotos] = useState([]); // array of /api/images/<id> URLs
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = productId ? { product_id: productId } : {};
      const res = await axios.get(`${API}/reviews`, { params });
      setReviews(res.data.reviews || []);
    } catch {
      // silent — show empty
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) return toast.error("Please pick a star rating");
    if (name.trim().length < 2) return toast.error("Enter your name");
    if (comment.trim().length < 4) return toast.error("Comment is too short");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, {
        product_id: productId || null,
        name,
        rating,
        comment,
        photos,
      });
      toast.success("Thanks — review posted!");
      setName(""); setComment(""); setRating(0); setPhotos([]); setShowForm(false);
      invalidateReviewSummary();
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting same file
    if (files.length === 0) return;
    const slotsLeft = MAX_PHOTOS - photos.length;
    if (slotsLeft <= 0) {
      toast.error(`You can attach up to ${MAX_PHOTOS} photos`);
      return;
    }
    const toUpload = files.slice(0, slotsLeft);
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        if (file.size > 8 * 1024 * 1024) {
          toast.error(`${file.name}: too large (max 8 MB)`);
          continue;
        }
        const form = new FormData();
        form.append("file", file);
        const res = await axios.post(`${API}/reviews/upload`, form, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      }
      if (uploaded.length > 0) {
        setPhotos((prev) => [...prev, ...uploaded]);
        toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} attached`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (url) => setPhotos((prev) => prev.filter((p) => p !== url));

  const absUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND}${u}` : u);

  // Close lightbox with Escape key
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  const count = reviews.length;
  const avg = count > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / count : 0;
  const breakdown = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    pct: count ? Math.round((reviews.filter((r) => r.rating === stars).length / count) * 100) : 0,
  }));

  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="mt-12 border-t border-white/5 pt-10"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-7">
        <div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#39FF14] font-bold mb-2">
            Researcher Feedback
          </p>
          <h2 className="font-headline text-2xl md:text-3xl font-black tracking-tighter">
            Reviews <span className="text-zinc-500 text-base">· {count}</span>
          </h2>
        </div>
        <Button
          onClick={() => setShowForm((v) => !v)}
          data-testid="open-review-form-btn"
          className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs shadow-[0_0_15px_rgba(57,255,20,0.35)]"
        >
          <MessageSquare className="w-4 h-4 mr-1.5" />
          {showForm ? "Cancel" : "Write a Review"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-6">
        {/* SUMMARY */}
        <aside className="bg-[#0A0F1A] border border-white/5 rounded-xl p-5 h-fit">
          <div className="text-center" data-testid="reviews-summary">
            <div
              className="font-headline text-5xl font-black text-[#39FF14] tabular-nums"
              style={{ textShadow: "0 0 14px rgba(57,255,20,0.5)" }}
            >
              {count ? avg.toFixed(1) : "—"}
            </div>
            <div className="mt-1">
              <Stars value={avg} size="w-5 h-5" dataTestid="avg-stars" />
            </div>
            <div className="text-xs text-zinc-500 uppercase tracking-wider mt-1">
              based on {count} review{count === 1 ? "" : "s"}
            </div>
          </div>
          <div className="mt-5 space-y-2">
            {breakdown.map((b) => (
              <div key={b.stars} className="flex items-center gap-2 text-xs">
                <span className="w-6 text-zinc-400">{b.stars}★</span>
                <div className="flex-1 bg-white/5 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="h-full bg-[#39FF14] rounded-full transition-all"
                    style={{ width: `${b.pct}%`, boxShadow: b.pct > 0 ? "0 0 8px rgba(57,255,20,0.4)" : "none" }}
                  />
                </div>
                <span className="w-9 text-right text-zinc-500 tabular-nums">{b.pct}%</span>
              </div>
            ))}
          </div>
        </aside>

        {/* LIST + FORM */}
        <div className="space-y-4">
          {showForm && (
            <form
              onSubmit={submit}
              data-testid="review-form"
              className="bg-[#0A0F1A] border border-[#39FF14]/30 rounded-xl p-5 space-y-3"
            >
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                  Your Rating
                </label>
                <div className="mt-1.5">
                  <Stars
                    value={rating}
                    size="w-7 h-7"
                    interactive
                    onChange={setRating}
                    dataTestid="rating-picker"
                  />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                  Name
                </label>
                <Input
                  data-testid="review-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Dr. J. Smith"
                  className="bg-black border-white/10 text-white mt-1.5"
                  maxLength={80}
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                  Comment
                </label>
                <Textarea
                  data-testid="review-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={3}
                  placeholder="Share your experience with this compound..."
                  className="bg-black border-white/10 text-white mt-1.5 text-sm"
                  maxLength={1000}
                />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-wider text-zinc-400 font-bold">
                  Photos <span className="text-zinc-600">(optional · up to {MAX_PHOTOS})</span>
                </label>
                <div className="mt-2 flex flex-wrap gap-2">
                  {photos.map((url) => (
                    <div key={url} className="relative w-16 h-16 rounded-md overflow-hidden border border-white/10 bg-black group">
                      <img src={absUrl(url)} alt="Review attachment" className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removePhoto(url)}
                        data-testid="remove-review-photo"
                        className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-red-400 hover:text-red-300"
                        aria-label="Remove photo"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  {photos.length < MAX_PHOTOS && (
                    <label
                      data-testid="review-photo-upload"
                      className="w-16 h-16 rounded-md border border-dashed border-white/15 hover:border-[#39FF14]/40 bg-black/40 flex items-center justify-center cursor-pointer transition text-zinc-500 hover:text-[#39FF14]"
                    >
                      {uploading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <ImagePlus className="w-5 h-5" />
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFiles}
                        disabled={uploading}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-review-btn"
                  className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs disabled:opacity-50"
                >
                  {submitting ? (
                    <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Posting</>
                  ) : (
                    "Post Review"
                  )}
                </Button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="text-zinc-500 text-sm flex items-center gap-2 py-6">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading reviews...
            </div>
          ) : reviews.length === 0 ? (
            <div className="bg-[#0A0F1A] border border-white/5 rounded-xl p-10 text-center" data-testid="reviews-empty">
              <Star className="w-8 h-8 text-zinc-700 mx-auto mb-3" />
              <p className="text-zinc-400 text-sm">No reviews yet — be the first researcher to leave one.</p>
            </div>
          ) : (
            reviews.map((r) => (
              <article
                key={r.id}
                data-testid="review-card"
                className="bg-[#0A0F1A] border border-white/5 rounded-xl p-5 hover:border-[#39FF14]/30 transition"
              >
                <div className="flex items-center justify-between gap-3 mb-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center font-bold text-[#39FF14] uppercase">
                      {r.name.charAt(0)}
                    </div>
                    <div>
                      <div className="font-semibold text-white text-sm">{r.name}</div>
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500">
                        {new Date(r.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  <Stars value={r.rating} />
                </div>
                <p className="text-zinc-300 text-sm leading-relaxed">{r.comment}</p>
                {r.photos && r.photos.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="review-photos">
                    {r.photos.map((url) => (
                      <button
                        key={url}
                        type="button"
                        onClick={() => setLightbox(absUrl(url))}
                        className="w-20 h-20 rounded-md overflow-hidden border border-white/10 hover:border-[#39FF14]/50 transition"
                        aria-label="Open photo"
                      >
                        <img src={absUrl(url)} alt="Review" className="w-full h-full object-cover" loading="lazy" />
                      </button>
                    ))}
                  </div>
                )}
              </article>
            ))
          )}
        </div>
      </div>

      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
          data-testid="review-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightbox}
            alt="Review"
            onClick={(e) => e.stopPropagation()}
            className="max-w-[95vw] max-h-[90vh] object-contain rounded-lg cursor-default"
          />
        </div>
      )}
    </section>
  );
}
